import { useToast } from '@dapp/multi-lib/ui/toast';
import { ButtonV2 } from '@dapp/multi-lib/ui/button';
import {
  NetworkChainIds,
  enabledNetworks,
  getNetworkFromChainId,
} from '@dapp/multi-lib/network';
import { MessageStatus } from '@layerzerolabs/scan-client';
import { erc20Abi } from 'viem';
import { useAtom } from 'jotai';
import { truncate } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  bridgeTokens,
  OftTypes,
} from 'structure/bridge/constants/bridgeTokens';
import { outboundAmountMultiplier } from 'structure/bridge/constants/constants';
import {
  DEFAULT_TOKEN_DECIMALS,
  layerZeroChainIdsByChainId,
  lzDefaultAdapterParams,
} from 'structure/bridge/constants/lazyerZeroChains';
import { bridgeAtom } from 'structure/bridge/store/bridgeAtom';
import {
  TTransaction,
  transactionsAtom,
} from 'structure/bridge/store/transactionsAtom';
import {
  encodeAbiParameters,
  formatUnits,
  maxUint256,
  parseAbiParameters,
  parseUnits,
  zeroAddress,
} from 'viem';
import {
  useBalance,
  useConnection,
  useReadContract,
  useWriteContract,
  useSimulateContract,
  useSwitchChain,
  useBlockNumber,
} from 'wagmi';
import PROXY_OFTV2_ABI from '../../constants/abis/ProxyOFTV2WithFee';
import INDIRECT_OFTV2_ABI from '../../constants/abis/indirectOFTv2WithFee';
import { BridgeInputField } from '../BridgeInputField/BridgeInputField';
import BridgeTransactions from '../BridgeTransactions/BridgeTransactions';
import { BridgeUserBalanceAndFee } from '../BridgeUserBalanceAndFee/BridgeUserBalanceAndFee';
import { ConnectButton } from 'components/ConnectButton';
import { useQueryClient } from '@tanstack/react-query';
import { ETH_AVERAGE_BLOCK_TIME } from 'utils/constants/ethConst';

const defaultEstimatedFee: [bigint, bigint] = [BigInt(0), BigInt(0)];

export const BridgePanel: React.FC = () => {
  const { t } = useTranslation('common');
  const toastV2 = useToast();

  const { chain } = useConnection();
  const { address: userAddress } = useConnection();
  const { data: userNativeBalance } = useBalance({
    address: userAddress,
  });

  const [bridgeState, setBridgeState] = useAtom(bridgeAtom);
  const [transactions, setTransactions] = useAtom(transactionsAtom);

  const [toAddressBytes, setToAddressBytes] = useState<
    `0x${string}` | undefined
  >();
  const [approvalToggle, setApprovalToggle] = useState<boolean>(false);
  const { switchChain } = useSwitchChain();

  const [rendered, setRendered] = useState(false);
  // set rendered on first mount
  useEffect(() => {
    setRendered(true);
  }, []);

  const allowedNetworks = useMemo(() => {
    const chainIds = bridgeTokens
      .filter((token) => token.enabled)
      .map((token) => Object.keys(token.address))
      .flat()
      .filter(
        (chain_id) =>
          enabledNetworks.findIndex((obj) => obj.id === Number(chain_id)) !== -1
      )
      .sort();
    const uniqueChains = [...new Set(chainIds)];
    return uniqueChains.map((chainId) => Number(chainId));
  }, []);

  const chainId = useMemo((): number => {
    if (chain?.id && allowedNetworks.includes(chain.id)) {
      return chain.id;
    }
    return allowedNetworks[0];
  }, [chain, allowedNetworks]);

  // min amount for sendFrom args
  // amount - token fee
  const minAmount = useMemo(() => {
    const amount = bridgeState.token?.decimals
      ? bridgeState.amount -
        parseFloat(
          formatUnits(bridgeState.tokenFee, bridgeState.token?.decimals)
        )
      : 0;
    return amount;
  }, [bridgeState.amount, bridgeState.tokenFee, bridgeState.token]);

  // set toAddressBytes for sendFrom usage
  useEffect(() => {
    if (userAddress) {
      const encodedData = encodeAbiParameters(parseAbiParameters('address'), [
        userAddress,
      ]);
      setToAddressBytes(encodedData);
    }
  }, [userAddress]);

  // checks if we enable contract calls. must have oft contract on current network and be connected
  const hasOftAndUserAndNetwork =
    !!chain?.id && // do we have a network
    !!userAddress && // do we have a user address
    !!bridgeState.token?.oftAddress[chainId]; // do we have oft contract on current network

  // is dst chain a proxy?
  const destinationOftIsProxy =
    bridgeState.token?.oftType[bridgeState.dstChainId] === OftTypes.Proxy;

  // get allowance of token in oft for sending
  // we monitor change in allowanceDataOft to set the approval toggle
  const { data: allowanceOft = BigInt(0), queryKey: queryKeyAllowance } =
    useReadContract({
      query: {
        enabled:
          hasOftAndUserAndNetwork && !!bridgeState.token?.address[chainId],
      },
      abi: erc20Abi,
      functionName: 'allowance',
      chainId: chainId,
      address: bridgeState.token?.address[chainId],
      args: [
        userAddress!,
        bridgeState.token?.oftAddress[chainId] ?? zeroAddress,
      ], // user wallet address
    });

  // force render on approval
  useEffect(() => {
    setApprovalToggle(!approvalToggle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowanceOft]);

  // Get user token balance
  const {
    data: userBalance = BigInt(0),
    queryKey: queryKeyBalanceOf,
    isError: isBalanceOfError,
  } = useReadContract({
    query: {
      enabled: !!bridgeState.token?.address[chainId],
    },
    abi: erc20Abi,
    functionName: 'balanceOf',
    chainId: chainId,
    address: bridgeState.token?.address[chainId],
    args: [userAddress!],
  });

  useEffect(() => {
    if (userBalance) {
      setBridgeState((prevState) => ({
        ...prevState,
        userBalance: userBalance,
      }));
      return;
    }

    if (isBalanceOfError) {
      setBridgeState((prevState) => ({
        ...prevState,
        userBalance: BigInt(0),
      }));
      return;
    }
  }, [userBalance, isBalanceOfError]);

  // Get oft contract outboundAmount if we have a proxy oft contract
  const {
    data: outboundAmountOnProxy = BigInt(0),
    queryKey: queryKeyOutboundAmount,
  } = useReadContract({
    query: {
      enabled:
        hasOftAndUserAndNetwork &&
        destinationOftIsProxy &&
        !!bridgeState.token?.oftAddress[bridgeState.dstChainId],
    },
    abi:
      bridgeState.token?.oftType?.[bridgeState.dstChainId] === OftTypes.Proxy
        ? PROXY_OFTV2_ABI
        : INDIRECT_OFTV2_ABI,
    functionName: 'outboundAmount',
    address: bridgeState.token?.oftAddress[bridgeState.dstChainId],
    chainId: bridgeState.dstChainId,
  });

  // get token fee quote
  const {
    data: tokenFee = BigInt(0),
    isLoading: isLoadingQuote,
    queryKey: queryKeyQuoteOFTFee,
  } = useReadContract({
    // only enable when we have address and amount
    query: {
      enabled:
        hasOftAndUserAndNetwork &&
        bridgeState.amount > 0 &&
        !!bridgeState.token?.decimals,
    },
    address: bridgeState.token?.oftAddress[chainId] ?? zeroAddress,
    chainId: chainId, // current chainId
    abi:
      bridgeState.token?.oftType?.[bridgeState.dstChainId] === OftTypes.Proxy
        ? PROXY_OFTV2_ABI
        : INDIRECT_OFTV2_ABI,
    functionName: 'quoteOFTFee',
    args: [
      layerZeroChainIdsByChainId[bridgeState?.dstChainId], // dest chainId ftm testnet
      parseUnits(bridgeState.amount.toString(), bridgeState.token?.decimals), // token amount
    ],
  });

  useEffect(() => {
    if (bridgeState.amount > 0) {
      setBridgeState((prevState) => ({
        ...prevState,
        tokenQuoteLoading: true,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoadingQuote, bridgeState.amount]);

  useEffect(() => {
    setBridgeState((prevState) => ({
      ...prevState,
      tokenFee: tokenFee,
      tokenQuoteLoading: isLoadingQuote,
    }));
  }, [tokenFee, isLoadingQuote]);

  const queryClient = useQueryClient();
  const { data: blockNumber } = useBlockNumber({
    watch: true,
    cacheTime: ETH_AVERAGE_BLOCK_TIME * 5,
  });

  useEffect(() => {
    queryClient.invalidateQueries({ queryKey: queryKeyQuoteOFTFee });
    queryClient.invalidateQueries({ queryKey: queryKeyAllowance });
    queryClient.invalidateQueries({ queryKey: queryKeyBalanceOf });
    queryClient.invalidateQueries({ queryKey: queryKeyOutboundAmount });
  }, [blockNumber, queryClient]);

  // Get native fee estimate
  const { data: estimatedSendFee = defaultEstimatedFee } = useReadContract({
    query: {
      enabled:
        hasOftAndUserAndNetwork &&
        bridgeState.amount > 0 &&
        !!bridgeState.token?.decimals &&
        !!bridgeState.dstChainId,
    },
    address: bridgeState.token?.oftAddress[chainId],
    chainId: chainId, // current chainId
    abi:
      bridgeState.token?.oftType?.[bridgeState.dstChainId] === OftTypes.Proxy
        ? PROXY_OFTV2_ABI
        : INDIRECT_OFTV2_ABI,
    functionName: 'estimateSendFee',
    args: [
      layerZeroChainIdsByChainId[bridgeState.dstChainId],
      toAddressBytes ?? '0x', // to address encoded
      parseUnits(
        bridgeState.amount.toString(),
        bridgeState.token?.decimals ?? 18
      ), // token amount to send
      false, // use zero token
      lzDefaultAdapterParams, // adapter params
    ],
  });

  useEffect(() => {
    setBridgeState((prevState) => ({
      ...prevState,
      nativeFee: estimatedSendFee[0],
    }));
  }, [estimatedSendFee[0]]);

  // Approve token in oft
  const approvalSimulate = useSimulateContract({
    query: {
      enabled: hasOftAndUserAndNetwork && allowanceOft < userBalance,
    },
    abi: erc20Abi,
    functionName: 'approve',
    chainId: chainId,
    address: bridgeState.token?.address[chainId],
    args: [bridgeState.token?.oftAddress[chainId] ?? zeroAddress, maxUint256],
  });

  const { writeContractAsync: approve } = useWriteContract();

  // prepare sendFrom
  const sendFromSimulate = useSimulateContract({
    query: {
      enabled:
        !(allowanceOft < bridgeState.amount) && // has allowance
        bridgeState.amount > 0 &&
        bridgeState.amount >= minAmount && // amount is big enough
        !!bridgeState.token?.oftAddress[chainId] && // has oft contract
        !!bridgeState.token?.decimals,
    }, // has token decimals
    chainId: chainId,
    address: bridgeState.token?.oftAddress[chainId],
    abi:
      bridgeState.token?.oftType?.[bridgeState.dstChainId] === OftTypes.Proxy
        ? PROXY_OFTV2_ABI
        : INDIRECT_OFTV2_ABI,
    functionName: 'sendFrom',
    args: [
      userAddress!, // 'from' address to send tokens
      layerZeroChainIdsByChainId[bridgeState.dstChainId], // dest chainId
      toAddressBytes ?? '0x', // to address encoded 'to' address to send tokens
      parseUnits(
        bridgeState.amount.toString(),
        bridgeState.token?.decimals ?? 18
      ), // amount
      parseUnits(minAmount.toString(), bridgeState.token?.decimals ?? 18), // min amount = amount - fee with oft token shared decimals
      {
        refundAddress: userAddress!,
        zroPaymentAddress: zeroAddress,
        adapterParams: '0x',
      },
    ],
    value: estimatedSendFee[0], // native fee,
  });

  const { writeContractAsync: sendFrom, status: sendStatus } =
    useWriteContract();

  const handleSwitchNetwork = () => {
    switchChain({ chainId: allowedNetworks[0] });
  };
  const handleApproveOft = async () => {
    try {
      const txHash = await approve?.(approvalSimulate.data!.request);

      toastV2({
        title: t('ERROR'),
        hash: truncate(txHash as string),
        description: truncate(txHash as string),
        type: 'success',
      });
    } catch (error) {
      console.log('### error', error);
      toastV2({
        title: t('ERROR'),
        description: (error as Error).message,
        type: 'success',
      });
    }
  };

  console.log('### sendFromSimulate', sendFromSimulate);

  const handleBridge = async () => {
    try {
      console.log('### sendFromSimulate', sendFromSimulate);
      const txHash = await sendFrom(
        sendFromSimulate.data!.request as Parameters<typeof sendFrom>[0]
      );

      const newTransaction: TTransaction = {
        status: MessageStatus.INFLIGHT,
        userAddress: userAddress!,
        srcChainId: chainId as NetworkChainIds,
        dstChainId: bridgeState.dstChainId as NetworkChainIds,
        toAddress: toAddressBytes ?? '0x',
        amount: parseUnits(
          bridgeState.amount.toString(),
          bridgeState.token?.decimals ?? 18
        ).toString(),
        minAmount: parseUnits(
          minAmount.toString(),
          bridgeState.token?.decimals ?? 18
        ).toString(),
        srcTxHash: txHash,
        dstTxHash: '0x',
        nativeFee: estimatedSendFee[0].toString(),
        token: bridgeState.token!,
        initialNonce: '0',
      };
      const previousTransactions = transactions;
      setTransactions([...previousTransactions, newTransaction]);

      // reset amounts on success
      const prevState = bridgeState;
      setBridgeState({
        ...prevState,
        amount: 0,
        tokenFee: BigInt(0),
      });

      // inform user
      toastV2({
        title:
          t('BRIDGE_SUCCESSFUL') +
          ' ' +
          getNetworkFromChainId(bridgeState.dstChainId).name,
        type: 'success',
        hash: truncate(txHash as string),
        description: 'Please wait for the transaction to be received.',
      });
    } catch (error) {
      console.log('### error', error);
      toastV2({
        title: t('ERROR'),
        description: (error as Error).message,
        type: 'success',
      });
    }
  };

  // if dst oft is a proxy...
  const maxBridgeAmountForProxy =
    outboundAmountOnProxy / BigInt(outboundAmountMultiplier); // max amount that can be bridged with proxy

  const hasEnoughProxyLiquidity =
    maxBridgeAmountForProxy >=
    parseUnits(
      bridgeState.amount.toString() ?? '0',
      bridgeState.token?.decimals ?? DEFAULT_TOKEN_DECIMALS
    ); // check if there is liquidity in proxy

  const bnTokenAmount = parseUnits(
    bridgeState.amount.toString(),
    bridgeState.token?.decimals ?? 18
  );

  const insufficientBalance =
    userBalance === BigInt(0) ||
    (bridgeState.userBalance < bnTokenAmount && bridgeState.amount > 0);

  const allowanceRequired =
    allowanceOft < bnTokenAmount || allowanceOft === BigInt(0);

  const userNativeValue = userNativeBalance?.value ?? BigInt(0);

  const disabled = allowanceRequired
    ? false // if user needs to approve then we don't disable
    : bridgeState.amount === 0 ||
      insufficientBalance ||
      bridgeState.amount < minAmount || // amount too low
      userNativeValue < estimatedSendFee[0] || // user has insufficient gas, when not connected value does not exist
      (!hasEnoughProxyLiquidity && destinationOftIsProxy) || // amount too big when dst oft is proxy
      bridgeState.tokenQuoteLoading || // app is loading quote
      (sendFromSimulate.isError && allowanceOft < bridgeState.amount) || // error in prepare for contract call and allowance big enough
      sendStatus == 'pending';

  const buttonAction = !allowedNetworks.includes(chain?.id ?? 0)
    ? handleSwitchNetwork
    : allowanceRequired
      ? handleApproveOft
      : handleBridge;

  const bridgeMessage: string = !allowedNetworks.includes(chain?.id ?? 0) // bridge disabled for destination chain
    ? t('migrate:SWITCH_NETWORK')
    : allowanceRequired // user has insufficient allowance
      ? t('APPROVE')
      : insufficientBalance
        ? t('INSUFFICIENT') + ' ' + t('BALANCE')
        : userNativeValue < estimatedSendFee[0] // user has insufficient gas balance, when not connected value does not exist
          ? t('INSUFFICIENT_GAS')
          : destinationOftIsProxy && !hasEnoughProxyLiquidity // destination oft is proxy and has insufficient liquidity
            ? t('BRIDGE_LIQUIDITY_ERROR')
            : 'Bridge'; // user can bridge

  return (
    <div className='flex w-full flex-col gap-5 bg-black p-8 md:w-1/2 '>
      <BridgeInputField chainId={chainId} allowedNetworks={allowedNetworks} />
      <BridgeUserBalanceAndFee
        tokenBalance={userBalance}
        tokenFee={tokenFee}
        nativeFee={estimatedSendFee[0]}
        maxBridgeAmountProxy={maxBridgeAmountForProxy} // set above
      />
      {rendered && !userAddress ? (
        <ConnectButton className='px-10 py-2 text-base' />
      ) : (
        rendered && (
          <BridgeActionButton
            title={bridgeMessage}
            disabled={disabled}
            action={buttonAction}
          />
        )
      )}
      {transactions.length > 0 && (
        <BridgeTransactions
          transactions={transactions}
          user={userAddress ?? '0x'}
        />
      )}
    </div>
  );
};

interface BridgeACtionButtonProps {
  title: string;
  disabled: boolean;
  action: () => void;
}

function BridgeActionButton({
  title,
  disabled,
  action,
}: BridgeACtionButtonProps) {
  return (
    <ButtonV2
      variant='default'
      variantStyle='quaternary'
      disabled={disabled}
      size='medium'
      className='font-haben !rounded-full !bg-[#FFFFFF] px-6 !text-box-primary'
      onClick={action}
    >
      {title}
    </ButtonV2>
  );
}
